import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./CompanyPageStyles.module.css"; // Your updated CSS module
import Wizard from "../../CompanyDashboard/CompanyContent/Wizard";

const CompanyPage = () => {
  // Retrieve company code from URL (id)
  const { id } = useParams();
  const admincode = id;
  const navigate = useNavigate();

  // Retrieve stored company name and image file from localStorage
  const companyName = localStorage.getItem("clickedCompanyName") || "No Company";
  const imageFile = localStorage.getItem("clickedImageFile") || "";
  const imageUrl = imageFile ? `https://starbehaviors.com/uploadimage/${imageFile}` : "";

  // State for assigned departments (from get_company_departments)
  const [departments, setDepartments] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [deptError, setDeptError] = useState("");

  // State for filled (ready) departments (from get_departments_by_company)
  const [filledDepts, setFilledDepts] = useState([]);

  // State for master department types (for assigning new ones)
  const [masterDepts, setMasterDepts] = useState([]);
  const [selectedDept, setSelectedDept] = useState("");
  const [assignMessage, setAssignMessage] = useState("");

  // Fetch assigned departments for this company
  const fetchAssignedDepartments = async () => {
    setLoadingDepts(true);
    setDeptError("");
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/get_company_departments",
        { companycode: admincode }
      );
      if (response.data && response.data.departments) {
        setDepartments(response.data.departments);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      console.error("Error fetching assigned departments:", error);
      setDeptError("No departments assigned.");
    }
    setLoadingDepts(false);
  };

  // Fetch filled (ready) departments
  const fetchFilledDepartments = async () => {
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/get_departments_by_company",
        { company_code: admincode }
      );
      if (Array.isArray(response.data)) {
        setFilledDepts(response.data);
      } else {
        setFilledDepts([]);
      }
    } catch (error) {
      console.error("Error fetching filled departments:", error);
    }
  };

  // Fetch master department types
  const fetchMasterDepartments = async () => {
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/get_master_assessment_type",
        {}
      );
      if (Array.isArray(response.data)) {
        setMasterDepts(response.data);
      } else {
        setMasterDepts([]);
      }
    } catch (error) {
      console.error("Error fetching master departments:", error);
    }
  };

  useEffect(() => {
    fetchAssignedDepartments();
    fetchFilledDepartments();
    fetchMasterDepartments();
  }, [admincode]);

  // Check if a department is ready (filled)
  const isDepartmentReady = (assignedDept) => {
    return filledDepts.some(
      (filled) => filled.company_department === assignedDept.departmentcode
    );
  };

  // Return the filled department object matching the assigned department
  const getFilledDepartment = (assignedDept) => {
    return filledDepts.find(
      (filled) => filled.company_department === assignedDept.departmentcode
    );
  };

  // Handle assignment of a new department (if needed)
  const handleAssignDepartment = async () => {
    if (!selectedDept) {
      setAssignMessage("Please select a department type.");
      return;
    }
    setAssignMessage("");
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/add_assessment_type",
        {
          companycode: admincode,
          departmentcode: selectedDept,
        }
      );
      if (response.data && response.data.message === "Department type added successfully.") {
        setAssignMessage("Department assigned successfully.");
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response.data && response.data.error) {
        setAssignMessage(response.data.error);
      } else {
        setAssignMessage("Failed to assign department.");
      }
    } catch (error) {
      console.error("Error assigning department:", error);
      setAssignMessage("Department already assigned");
    }
  };

  // Navigate to fill details page for a department that is not ready
  const handleFillDetails = (dept) => {
    navigate(`/admin/view-company/${admincode}/company-assessment/${dept.departmentcode}`);
  };

  // Open the review page in a new tab for a filled department
  const handleShowReview = (filledDept) => {
    if (filledDept && filledDept.admincode) {
      window.open(`/user-landingpage/${filledDept.admincode}`, "_blank");
    }
  };

  return (
    <div className={styles.companyPageContainer}>
      <Wizard currentStep={2} />
      <h1 className={styles.companyPageTitle}>{companyName}</h1>
      {imageUrl && (
        <div className={styles.imageWrapper}>
          <img src={imageUrl} alt={companyName} className={styles.companyImage} />
        </div>
      )}

      <hr className={styles.sectionDivider} />

      <div className={styles.departmentsSection}>
        <h2 className={styles.sectionHeading}>Departments</h2>
        {loadingDepts ? (
          <p>Loading departments...</p>
        ) : deptError ? (
          <p>{deptError}</p>
        ) : departments.length === 0 ? (
          <p>No Department assigned</p>
        ) : (
          <div className={styles.departmentCards}>
            {departments.map((dept) => {
              const ready = isDepartmentReady(dept);
              // Look up master department name by comparing dept.departmentcode with masterDepts.assessment_type_id
              const matchingMaster = masterDepts.find(
                (master) => master.assessment_type_id === dept.departmentcode
              );
              const departmentName = matchingMaster ? matchingMaster.AssessmentType : "Unknown Department";
              const filledDept = getFilledDepartment(dept);
              return (
                <div
                  key={dept.department_assignment_id}
                  className={styles.departmentCard}
                >
                  <p className={styles.departmentName}>{departmentName}</p>
                  {ready ? (
                    <div>
                      <p className={styles.readyStatus}>
                        <span className={styles.readyDot}></span>
                        Assessment Ready
                      </p>
                      <button
                        className={styles.actionButton}
                        onClick={() => handleShowReview(filledDept)}
                      >
                        Show preview
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className={styles.notReadyStatus}>
                        <span className={styles.notReadyDot}></span>
                        Not Filled
                      </p>
                      <button
                        className={styles.actionButton}
                        onClick={() => handleFillDetails(dept)}
                      >
                        Fill Details
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <hr className={styles.sectionDivider} />

      <div className={styles.assignSection}>
        <h2 className={styles.sectionHeading}>Assign Department</h2>
        <div className={styles.assignFormRow}>
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            className={styles.assignSelect}
          >
            <option value="">Select Department</option>
            {masterDepts.map((dept) => (
              <option key={dept.assessment_type_id} value={dept.assessment_type_id}>
                {dept.AssessmentType}
              </option>
            ))}
          </select>
          <button onClick={handleAssignDepartment} className={styles.assignButton}>
            Assign
          </button>
        </div>
        {assignMessage && <p className={styles.assignMessage}>{assignMessage}</p>}
      </div>
    </div>
  );
};

export default CompanyPage;
